import { SiteHeader as SiteHeaderComponent } from "@xapp/components";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { UserProfile } from "auth/modules/state";
import { AppSwitcherContainer } from "components/AppSwitcher";
import { NavDrawer } from "components/NavDrawer";
import { NAV_DRAWER_ID } from "components/NavDrawer/components/NavDrawer";
import { isAppOnlyUser, OPEN_NAV_DRAWER_EVENT } from "components/NavDrawer/utils";
import { NAV_DRAWER_WIDTHS, useNavDrawerMode } from "components/NavDrawer/useNavDrawerMode";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrgSwitcherContainer } from "components/OrgSwitcher";
import { App, useGetOrgsQuery } from "generated/graphql";
import React, { FC, useCallback, useEffect, useState, PropsWithChildren } from "react";
import style from "./style.scss";
import { setTimesSeen } from "./utils";

export type SiteHeaderApp = Pick<App, "name" | "organizationId" | "appId">;

export interface SiteHeaderProps {
    readonly asUser?: boolean;
    readonly user: UserProfile;
    onChangePerspective(): void;
}

export const SiteHeader: FC<PropsWithChildren<SiteHeaderProps>> = props => {
    const { asUser } = props;

    // Wide screens: the drawer docks beside the page (full, icons only, or hidden), remembered per
    // user. Narrow screens: it overlays the page and closes after use.
    const docked = useMediaQuery("(min-width:900px)");
    // A user who reaches only their own business (a get.xapp.ai dealer) gets one card in the
    // drawer and nothing else, so it starts closed for them; the hamburger still opens it.
    const [mode, setMode] = useNavDrawerMode(props.user?.userId, isAppOnlyUser(props.user) ? "hidden" : "expanded");
    const [showNavDrawer, setShowNavDrawer] = useState<boolean>(false);

    // Shift the page content by the docked drawer's width.
    useEffect(() => {
        const width = docked && props.user ? NAV_DRAWER_WIDTHS[mode] : 0;
        document.documentElement.style.setProperty("--studio-nav-width", `${width}px`);
    }, [docked, mode, props.user]);

    const [getOrgs] = useGetOrgsQuery({
        requestPolicy: "cache-and-network",
        variables: {
            from: 0,
            size: 12
        }
    });

    const { data } = getOrgs;

    // If they only have one org, don't display it
    const totalOrgs = data?.orgs?.total;

    useEffect(setTimesSeen, []);

    // Anything can ask for the drawer, e.g. the old org bar's "Show me".
    useEffect(() => {
        const open = () => (docked ? setMode("expanded") : setShowNavDrawer(true));
        window.addEventListener(OPEN_NAV_DRAWER_EVENT, open);
        return () => window.removeEventListener(OPEN_NAV_DRAWER_EVENT, open);
    }, [docked, setMode]);

    function handleToggleNavDrawer() {
        if (docked) {
            // Hamburger: full <-> icons; from hidden it brings the full menu back.
            setMode(mode === "expanded" ? "mini" : "expanded");
            return;
        }
        setShowNavDrawer(val => !val);
    }

    // Closing is explicit, never a toggle: two close signals at once (a link click and the route
    // change it causes) must not cancel out and leave the drawer's state inverted.
    const handleCloseNavDrawer = useCallback(() => (docked ? setMode("hidden") : setShowNavDrawer(false)), [docked, setMode]);
    const handleCollapseNavDrawer = useCallback(() => setMode("mini"), [setMode]);
    const handleExpandNavDrawer = useCallback(() => setMode("expanded"), [setMode]);
    const drawerOpen = docked ? mode !== "hidden" : showNavDrawer;

    function renderAsUserNotice() {
        if (asUser) {
            return (
                <p className={style.asUserNotice}>
                    You are viewing this page as a user.{" "}
                    <button onClick={props.onChangePerspective}>
                        Click here
                    </button>{" "}
                    to return to the Admin view
                </p>
            );
        }
    }

    return (
        <>
            <SiteHeaderComponent>
                {/* Org navigation lives in the drawer now, so everyone gets the menu. */}
                {/* The docked drawer carries its own menu button; show this one when it is hidden or on phones. */}
                {props.user && !(docked && mode !== "hidden") && (
                    <IconButton
                        sx={{ color: "#fff", mx: 1 }}
                        aria-label="Open navigation menu"
                        aria-controls={NAV_DRAWER_ID}
                        aria-expanded={docked ? mode === "expanded" : showNavDrawer}
                        aria-haspopup="true"
                        onClick={handleToggleNavDrawer}
                    >
                        <MenuIcon />
                    </IconButton>
                )}
                <div className={style.contentWrap}>
                    <div className={style.orgSwitcher}>
                        {totalOrgs > 1 ? <OrgSwitcherContainer /> : <></>}
                    </div>
                    <div className={style.appSwitcher}>
                        <AppSwitcherContainer />
                    </div>
                </div>

                <div className={style.headerNav}>
                    {props.children}
                </div>
            </SiteHeaderComponent>

            {renderAsUserNotice()}

            {/* Only render the NavDrawer if we have a user profile & orgs */}
            {// only render the Nav Drawer if there is a user profile in state
                props.user && (
                    <NavDrawer
                        isActive={drawerOpen}
                        onToggleClick={handleToggleNavDrawer}
                        onClose={handleCloseNavDrawer}
                        docked={docked}
                        mini={docked && mode === "mini"}
                        onCollapse={handleCollapseNavDrawer}
                        onExpand={handleExpandNavDrawer}
                    />
                )
            }
        </>
    );

};
